import React, { useEffect, useState, useCallback } from "react";
import Swal from "sweetalert2";
import {
    Truck,
    Send,
    X,
    Phone,
    MapPin,
    FileText,
    IndianRupee,
    Clock,
    ArrowRightLeft,
    Download,
    CheckCircle2,
    Loader2,
} from "lucide-react";

import api from "../api/axios";

const TRANSFERED_API =
  "/admin/border-tax/three/three-month-tax/transferred-by-me?tax_type=three_month_tax";

const STATUS_STYLES = {
    "pending-at-payment-of-checking-fee": "bg-gray-100 text-gray-600",
    "tax-checking": "bg-amber-50 text-amber-700",
    final_payment_pending: "bg-orange-50 text-orange-700",
    final_payment_done: "bg-blue-50 text-blue-700",
    completed: "bg-emerald-50 text-emerald-700",
};

const statusLabel = (s) =>
    (s || "-")
        .split(/[-_]/)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");

const StatusBadge = ({ status }) => (
    <span
        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
            STATUS_STYLES[status] || "bg-gray-100 text-gray-600"
        }`}
    >
        {statusLabel(status)}
    </span>
);

const Row = ({ label, value }) => (
    <div className="flex items-start justify-between gap-4 py-2 border-b border-gray-100 last:border-0">
        <span className="text-xs text-gray-500">{label}</span>
        <span className="text-sm font-medium text-gray-900 text-right break-all">
            {value ?? "-"}
        </span>
    </div>
);

const DetailModal = ({ item, onClose }) => {
    if (!item) return null;

    const lastTransfer =
        item.transfer_history?.[item.transfer_history.length - 1];

    return (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4">
            <div className="bg-white w-full sm:max-w-2xl sm:rounded-2xl rounded-t-2xl max-h-[92vh] flex flex-col shadow-xl">
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-indigo-50 flex items-center justify-center">
                            <Truck className="w-4 h-4 text-indigo-600" />
                        </div>
                        <div>
                            <p className="text-sm font-semibold text-gray-900">
                                {item.vehicle_number || "-"}
                            </p>
                            <p className="text-xs text-gray-500">
                                {item.main_state?.main_state || "-"} · {item.seat_type || "-"}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="w-9 h-9 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-500"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="overflow-y-auto px-5 py-4 space-y-6">
                    <div className="flex items-center justify-between">
                        <StatusBadge status={item.tax_status} />
                        {item.border_tax_pdf?.url && (
                            <a
                                href={item.border_tax_pdf.url}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-1.5 text-xs font-medium text-indigo-600 hover:text-indigo-800"
                            >
                                <Download className="w-3.5 h-3.5" />
                                Download Tax PDF
                            </a>
                        )}
                    </div>

                    {/* Vehicle */}
                    <section>
                        <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">
                            Vehicle Details
                        </h3>
                        <Row label="Chassis No." value={item.chassis_number} />
                        <Row label="Tax Type" value={statusLabel(item.tax_type)} />
                        <Row label="Duration" value={item.duration || "-"} />
                    </section>

                    {/* Payment */}
                    <section>
                        <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1 flex items-center gap-1">
                            <IndianRupee className="w-3.5 h-3.5" /> Payment
                        </h3>
                        <Row
                            label="Final Tax Amount"
                            value={`₹${item.final_tax_amount ?? 0}`}
                        />
                        <Row label="Service Fee" value={`₹${item.service_fee ?? 0}`} />
                        <Row
                            label="Total Payable"
                            value={`₹${item.total_pay_able_fee ?? 0}`}
                        />
                        <Row
                            label="Razorpay Order ID"
                            value={item.checking_tax_payment?.razorpay_order_id}
                        />
                        <Row
                            label="Razorpay Payment ID"
                            value={item.checking_tax_payment?.razorpay_payment_id}
                        />
                        <Row
                            label="Payment Confirmed"
                            value={
                                item.payment_done_check_time
                                    ? new Date(item.payment_done_check_time).toLocaleString()
                                    : "-"
                            }
                        />
                    </section>

                    {/* Assigned Partner */}
                    <section>
                        <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1 flex items-center gap-1">
                            <Phone className="w-3.5 h-3.5" /> Assigned Provider
                        </h3>
                        <Row label="Name" value={item.assigned_partner?.name} />
                        <Row
                            label="WhatsApp"
                            value={item.assigned_partner?.whatsapp_number}
                        />
                    </section>

                    {/* Transfer history */}
                    {item.transfer_history?.length > 0 && (
                        <section>
                            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2 flex items-center gap-1">
                                <ArrowRightLeft className="w-3.5 h-3.5" /> Transfer History
                            </h3>
                            <div className="space-y-3">
                                {item.transfer_history.map((t, i) => (
                                    <div
                                        key={t._id || i}
                                        className="bg-gray-50 rounded-lg p-3 text-xs"
                                    >
                                        <p className="text-gray-900 font-medium">
                                            {t.from_provider} → {t.to_provider}
                                        </p>
                                        <p className="text-gray-500 mt-0.5">
                                            Reason: {t.reason || "-"}
                                        </p>
                                        <p className="text-gray-400 mt-0.5">
                                            {new Date(t.transferred_at).toLocaleString()}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* Status timeline */}
                    {item.statusHistory?.length > 0 && (
                        <section>
                            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2 flex items-center gap-1">
                                <Clock className="w-3.5 h-3.5" /> Status Timeline
                            </h3>
                            <div className="relative pl-4 border-l-2 border-gray-100 space-y-4">
                                {item.statusHistory.map((h, i) => (
                                    <div key={i} className="relative">
                                        <span className="absolute -left-[21px] top-0.5 w-3 h-3 rounded-full bg-indigo-500 ring-4 ring-white" />
                                        <div className="flex items-center gap-2">
                                            <StatusBadge status={h.status} />
                                            <span className="text-xs text-gray-400">
                                                {new Date(h.changedAt).toLocaleString()}
                                            </span>
                                        </div>
                                        {h.remark && (
                                            <p className="text-xs text-gray-500 mt-1">{h.remark}</p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}
                </div>

                <div className="px-5 py-3 border-t border-gray-100">
                    <button
                        onClick={onClose}
                        className="w-full py-2.5 rounded-xl bg-gray-900 text-white text-sm font-medium hover:bg-gray-800"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

const TransferedTaxesThree = () => {
    const [loading, setLoading] = useState(false);
    const [requests, setRequests] = useState([]);
    const [selected, setSelected] = useState(null);

    const fetchTransferredRequests = useCallback(async () => {
        try {
            setLoading(true);

            const { data } = await api.get(TRANSFERED_API);

            if (data.success) {
                setRequests(data.data || []);
            }
        } catch (error) {
            console.error(error);

            Swal.fire({
                icon: "error",
                title: "Error",
                text:
                    error.response?.data?.message ||
                    "Failed to fetch transferred requests.",
            });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchTransferredRequests();
    }, [fetchTransferredRequests]);

    return (
        <div className="p-4 sm:p-6">
            <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center">
                        <Send className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div>
                        <h1 className="text-lg font-semibold text-gray-900">
                            Three Month tax Requests
                        </h1>
                        <p className="text-xs text-gray-500">
                            Three Month tax requests transferred by you
                        </p>
                    </div>
                </div>
                <span className="text-xs font-medium px-3 py-1.5 rounded-full bg-gray-100 text-gray-600">
                    {requests.length} total
                </span>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                {loading ? (
                    <div className="p-10 flex flex-col items-center gap-2 text-gray-400">
                        <Loader2 className="w-6 h-6 animate-spin" />
                        <span className="text-sm">Loading requests...</span>
                    </div>
                ) : requests.length === 0 ? (
                    <div className="p-10 flex flex-col items-center gap-2 text-gray-400">
                        <FileText className="w-8 h-8" />
                        <span className="text-sm">No transferred requests found.</span>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full">
                            <thead className="bg-gray-50">
                                <tr>
                                    {[
                                        "#",
                                        "Vehicle",
                                        "Chassis No.",
                                        "State",
                                        "Amount",
                                        "Status",
                                        "Provider",
                                        "Transferred On",
                                        "",
                                    ].map((h) => (
                                        <th
                                            key={h}
                                            className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3 whitespace-nowrap"
                                        >
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>

                            <tbody className="divide-y divide-gray-100">
                                {requests.map((item, index) => (
                                    <tr
                                        key={item._id}
                                        className="hover:bg-gray-50/70 transition-colors"
                                    >
                                        <td className="px-4 py-3 text-sm text-gray-500">
                                            {index + 1}
                                        </td>

                                        <td className="px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">
                                            {item.vehicle_number || "-"}
                                        </td>

                                        <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                                            {item.chassis_number || "-"}
                                        </td>

                                        <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                                            <span className="inline-flex items-center gap-1">
                                                <MapPin className="w-3.5 h-3.5 text-gray-400" />
                                                {item.main_state?.main_state || "-"}
                                            </span>
                                        </td>

                                        <td className="px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">
                                            ₹{item.total_pay_able_fee ?? 0}
                                        </td>

                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <StatusBadge status={item.tax_status} />
                                        </td>

                                        <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                                            {item.assigned_partner?.name || "-"}
                                        </td>

                                        <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                                            {item.transfer_history?.length
                                                ? new Date(
                                                      item.transfer_history[
                                                          item.transfer_history.length - 1
                                                      ].transferred_at
                                                  ).toLocaleString()
                                                : "-"}
                                        </td>

                                        <td className="px-4 py-3 text-right whitespace-nowrap">
                                            <button
                                                onClick={() => setSelected(item)}
                                                className="inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:text-white hover:bg-indigo-600 border border-indigo-200 hover:border-indigo-600 px-3 py-1.5 rounded-lg transition-colors"
                                            >
                                                <CheckCircle2 className="w-3.5 h-3.5" />
                                                View
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <DetailModal item={selected} onClose={() => setSelected(null)} />
        </div>
    );
};

export default TransferedTaxesThree;